import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  GridView as GridIcon,
  Article as PageIcon,
  Chat as AIChatIcon,
} from '@mui/icons-material';
import { useCanvasBySlug } from '../../data/canvas/useCanvases';
import { useCreatePage } from '../../data/canvas/usePages';
import { LayoutType } from '../../types/canvas.types';

interface PageData {
  title: string;
  slug: string;
  layout_type: LayoutType;
}

const LAYOUT_TYPES = [
  {
    type: 'grid' as LayoutType,
    name: 'Grid Layout',
    description: 'Flexible grid system for organizing widgets.',
    icon: <GridIcon />,
  },
  {
    type: 'page' as LayoutType,
    name: 'Page Layout',
    description: 'Traditional page layout with sections.',
    icon: <PageIcon />,
  },
  {
    type: 'ai_chat' as LayoutType,
    name: 'AI Chat Layout',
    description: 'Interactive AI conversation interface.',
    icon: <AIChatIcon />,
  },
];

export const PageWizard: React.FC = () => {
  console.log('🎯 PageWizard component is rendering!');

  // Get URL parameters
  const { accountId, canvasSlug } = useParams<{
    accountId: string;
    canvasSlug: string;
  }>();
  const navigate = useNavigate();

  // State management
  const [pageData, setPageData] = useState<PageData>({
    title: '',
    slug: '',
    layout_type: 'grid',
  });

  // Get canvas data
  const {
    data: canvas,
    isLoading: canvasLoading,
    error: canvasError,
  } = useCanvasBySlug(accountId!, canvasSlug!);

  // API hooks
  const createPageMutation = useCreatePage(accountId!, canvas?.id || '');

  // Auto-generate slug from title
  const generateSlug = (title: string): string => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  // Handle title change and auto-generate slug
  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = event.target.value;
    setPageData((prev) => ({
      ...prev,
      title: newTitle,
      slug: generateSlug(newTitle),
    }));
  };

  // Handle slug change (manual override)
  const handleSlugChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSlug = generateSlug(event.target.value);
    setPageData((prev) => ({
      ...prev,
      slug: newSlug,
    }));
  };

  // Handle form submission
  const handleCreatePage = async () => {
    if (!canvas) return;

    try {
      console.log('🚀 Creating page:', pageData);
      const newPage = await createPageMutation.mutateAsync({
        title: pageData.title,
        slug: pageData.slug,
        layout_type: pageData.layout_type,
      });

      console.log('✅ Page created successfully:', newPage);

      // Navigate to the new page
      navigate(`/${accountId}/canvases/${canvasSlug}/${newPage.slug}`);
    } catch (error) {
      console.error('Failed to create page:', error);
    }
  };

  // Loading state
  if (canvasLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading canvas...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (canvasError || !canvas) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error loading canvas: {canvasError?.message || 'Canvas not found'}
        </Alert>
      </Box>
    );
  }

  // Main form
  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Create New Page
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Add a new page to "{canvas.name}"
        </Typography>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Page Title"
              placeholder="My Dashboard"
              value={pageData.title}
              onChange={handleTitleChange}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="URL Slug"
              placeholder="my-dashboard"
              value={pageData.slug}
              onChange={handleSlugChange}
              helperText="This will be used in the URL"
              required
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Choose Layout Type
          </Typography>
          <Grid container spacing={2}>
            {LAYOUT_TYPES.map((layout) => (
              <Grid item xs={12} sm={6} md={4} key={layout.type}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    border: pageData.layout_type === layout.type ? 2 : 1,
                    borderColor:
                      pageData.layout_type === layout.type
                        ? 'primary.main'
                        : 'divider',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: 2,
                    },
                  }}
                  onClick={() =>
                    setPageData((prev) => ({
                      ...prev,
                      layout_type: layout.type,
                    }))
                  }
                >
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <Box
                      sx={{
                        mb: 1,
                        color:
                          pageData.layout_type === layout.type
                            ? 'primary.main'
                            : 'text.secondary',
                      }}
                    >
                      {layout.icon}
                    </Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {layout.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {layout.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleCreatePage}
            disabled={
              !pageData.title || !pageData.slug || createPageMutation.isPending
            }
            size="large"
          >
            {createPageMutation.isPending ? 'Creating...' : 'Create Page'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/${accountId}/canvases/${canvasSlug}`)}
            size="large"
          >
            Cancel
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
